import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, fmtMoney, fmtDate } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { QuoteStatusBadge } from '@/components/QuoteStatusBadge';
import { Plus, Search, FileText, ChevronRight } from 'lucide-react';
import { EmptyState } from '@/components/EmptyState';

export default function QuotesListPage() {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState('all');

  const load = async () => {
    setLoading(true);
    try { setQuotes((await api.get('/quotes')).data || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const isExpired = (q) => new Date(q.expiry_date) < new Date();

  const filtered = quotes.filter((q) => {
    const term = query.trim().toLowerCase();
    const matchTerm = !term || [q.quote_number, q.client_snapshot?.name, q.title].some((s) => (s || '').toLowerCase().includes(term));
    let matchTab = true;
    if (tab === 'draft') matchTab = q.status === 'draft';
    else if (tab === 'sent') matchTab = q.status === 'sent';
    else if (tab === 'accepted') matchTab = q.status === 'accepted';
    else if (tab === 'rejected') matchTab = q.status === 'rejected';
    else if (tab === 'expired') matchTab = isExpired(q);
    return matchTerm && matchTab;
  });

  return (
    <div>
      <PageHeader
        title="Presupuestos"
        subtitle="Gestiona y consulta todos tus presupuestos."
        actions={
          <Link to="/quotes/new">
            <Button className="btn-primary h-10" data-testid="quotes-list-new-button">
              <Plus className="mr-1.5 h-4 w-4" /> Nuevo presupuesto
            </Button>
          </Link>
        }
      />

      <div className="glass overflow-hidden rounded-xl">
        <div className="flex flex-col gap-3 border-b border-[var(--glass-border)] p-3 sm:flex-row sm:items-center">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="bg-[var(--surface-1)]">
              <TabsTrigger value="all" data-testid="quotes-tab-all">Todos</TabsTrigger>
              <TabsTrigger value="draft" data-testid="quotes-tab-draft">Borradores</TabsTrigger>
              <TabsTrigger value="sent" data-testid="quotes-tab-sent">Enviados</TabsTrigger>
              <TabsTrigger value="accepted" data-testid="quotes-tab-accepted">Aceptados</TabsTrigger>
              <TabsTrigger value="rejected" data-testid="quotes-tab-rejected">Rechazados</TabsTrigger>
              <TabsTrigger value="expired" data-testid="quotes-tab-expired">Vencidos</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="ml-auto flex flex-1 items-center gap-2 sm:max-w-xs">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar por #, cliente, título…" className="h-9 border-0 bg-transparent focus-visible:ring-0" data-testid="quotes-table-search-input" />
          </div>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Cargando…</div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={FileText}
            title="Sin presupuestos"
            description="Crea tu primer presupuesto en menos de un minuto."
            action={<Link to="/quotes/new"><Button className="btn-primary"><Plus className="mr-1.5 h-4 w-4" /> Crear</Button></Link>}
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[140px]">#</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Emisión</TableHead>
                <TableHead>Vence</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="w-[40px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((q) => (
                <TableRow
                  key={q.id}
                  className="cursor-pointer hover:bg-[var(--surface-1)]"
                  onClick={() => navigate(`/quotes/${q.id}`)}
                  data-testid={`quotes-row-${q.quote_number}`}
                >
                  <TableCell className="font-mono text-sm tabular-nums">{q.quote_number}</TableCell>
                  <TableCell>
                    <div className="font-medium">{q.client_snapshot?.name}</div>
                    {q.title && <div className="text-xs text-muted-foreground">{q.title}</div>}
                  </TableCell>
                  <TableCell><QuoteStatusBadge status={q.status} expired={isExpired(q)} /></TableCell>
                  <TableCell className="text-sm text-muted-foreground">{fmtDate(q.issue_date)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{fmtDate(q.expiry_date)}</TableCell>
                  <TableCell className="text-right font-mono font-semibold tabular-nums">{fmtMoney(q.total, q.currency)}</TableCell>
                  <TableCell><ChevronRight className="h-4 w-4 text-muted-foreground" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
